import React, { useEffect, useRef } from 'react';
import { Navbar as BootstrapNavbar, Nav, Container, NavDropdown, Button } from 'react-bootstrap';
import { LinkContainer } from 'react-router-bootstrap';
import { useAuth } from '../context/AuthContext';
import { userCanAccessPurchaseTabs, userCanManageOpportunities } from '../constants/opportunityAccess';
import { useTheme } from '../context/ThemeContext';
import { useArea } from '../context/AreaContext';
import { useLocation, useNavigate } from 'react-router-dom';
import type { AreaType } from '../context/AreaContext';
import { getAreaHomePath, shouldLeaveRouteOnAreaChange } from '../utils/areaRoutes';
import { clearUserAreaSelection, markUserAreaSelection } from '../utils/areaUserSelection';
import packageJson from '../../package.json';
import { isChangeManager, canViewChangeSecurityAudit } from '../pages/changes/changePermissions';

/** Versão estável da UI: build (REACT_APP_VERSION) ou campo version do package.json. */
const APP_DISPLAY_VERSION = (process.env.REACT_APP_VERSION || packageJson.version || '0.0.0').trim();

const Navbar: React.FC = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const changeManager = isChangeManager(user);
  const canViewChangeAudit = canViewChangeSecurityAudit(user);
  const { theme, toggleTheme } = useTheme();
  const { currentArea, setArea } = useArea();
  const navigate = useNavigate();
  const location = useLocation();

  const handleAreaChange = (newArea: AreaType) => {
    if (newArea === currentArea) {
      return;
    }
    markUserAreaSelection(newArea);
    setArea(newArea);
    if (shouldLeaveRouteOnAreaChange(location.pathname, newArea)) {
      navigate(getAreaHomePath(newArea), { replace: true });
    } else {
      clearUserAreaSelection();
    }
  };
  const navbarRef = useRef<HTMLDivElement | null>(null);
  const canAccessPurchaseTabs = userCanAccessPurchaseTabs(user);

  useEffect(() => {
    const updateNavbarHeight = () => {
      const navbarHeight = navbarRef.current?.offsetHeight;
      if (!navbarHeight) return;
      document.documentElement.style.setProperty('--app-navbar-height', `${navbarHeight}px`);
    };

    updateNavbarHeight();
    window.addEventListener('resize', updateNavbarHeight);

    return () => {
      window.removeEventListener('resize', updateNavbarHeight);
    };
  }, [isAuthenticated, currentArea, user?.role, user?.can_access_purchase_tabs, user?.name]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const canSeeProjectsArea = user?.role === 'admin' || Boolean(user?.can_access_projects);
  const canSeeProjectsCosts = user?.role === 'admin' || user?.role === 'analista';

  return (
    <BootstrapNavbar ref={navbarRef} variant="dark" expand="lg" fixed="top" className="app-navbar">
      <Container fluid className="app-navbar-inner">
        <LinkContainer to={getAreaHomePath(currentArea)}>
          <BootstrapNavbar.Brand className="app-navbar-brand">
            <span className="app-navbar-brand-text">SUPORTNECT</span>
            <span className="app-navbar-version" title={`Versão da aplicação (${APP_DISPLAY_VERSION})`}>
              v{APP_DISPLAY_VERSION}
            </span>
          </BootstrapNavbar.Brand>
        </LinkContainer>
        <BootstrapNavbar.Toggle aria-controls="basic-navbar-nav" />
        <BootstrapNavbar.Collapse id="basic-navbar-nav">
          {isAuthenticated ? (
            <>
              <Nav className="me-auto">
                {currentArea !== 'projects' && currentArea !== 'changes' && (
                  <LinkContainer to="/dashboard">
                    <Nav.Link>Dashboard</Nav.Link>
                  </LinkContainer>
                )}
                {currentArea === 'projects' ? (
                  <>
                    <LinkContainer to="/projects">
                      <Nav.Link>Projetos (Jira)</Nav.Link>
                    </LinkContainer>
                    <LinkContainer to="/projects/compras-e">
                      <Nav.Link>Compras-e</Nav.Link>
                    </LinkContainer>
                    {canSeeProjectsCosts && (
                      <LinkContainer to="/projects/costs">
                        <Nav.Link>Custos de Projetos</Nav.Link>
                      </LinkContainer>
                    )}
                  </>
                ) : currentArea === 'tickets' ? (
                  <>
                    <LinkContainer to="/tickets">
                      <Nav.Link>Tickets</Nav.Link>
                    </LinkContainer>
                    <NavDropdown title="Relatórios" id="reports-dropdown-tickets">
                      <LinkContainer to="/reports">
                        <NavDropdown.Item>Kanban</NavDropdown.Item>
                      </LinkContainer>
                      <LinkContainer to="/reports/dashboard">
                        <NavDropdown.Item>Dashboard</NavDropdown.Item>
                      </LinkContainer>
                    </NavDropdown>
                    <LinkContainer to="/ticket-export">
                      <Nav.Link>Exportar Tickets</Nav.Link>
                    </LinkContainer>
                  </>
                ) : currentArea === 'changes' ? (
                  <>
                    {/* end: evita marcar "Mudanças" ativo em /changes/dashboard, /approvals, etc. */}
                    <LinkContainer to="/changes" end>
                      <Nav.Link>Mudanças</Nav.Link>
                    </LinkContainer>
                    {changeManager ? (
                      <LinkContainer to="/changes/calendar">
                        <Nav.Link>Calendário</Nav.Link>
                      </LinkContainer>
                    ) : null}
                    {changeManager ? (
                      <LinkContainer to="/changes/dashboard">
                        <Nav.Link>Indicadores</Nav.Link>
                      </LinkContainer>
                    ) : null}
                    {canViewChangeAudit ? (
                      <LinkContainer to="/changes/auditoria">
                        <Nav.Link>Auditoria</Nav.Link>
                      </LinkContainer>
                    ) : null}
                  </>
                ) : (
                  <>
                    <LinkContainer to="/purchases">
                      <Nav.Link>Requisições de Compras</Nav.Link>
                    </LinkContainer>
                    <LinkContainer to="/suppliers">
                      <Nav.Link>Fornecedores</Nav.Link>
                    </LinkContainer>
                    {canAccessPurchaseTabs && (
                      <LinkContainer to="/purchases/opportunities">
                        <Nav.Link>Oportunidades</Nav.Link>
                      </LinkContainer>
                    )}
                    <LinkContainer to="/documents">
                      <Nav.Link>Documentos</Nav.Link>
                    </LinkContainer>
                    <LinkContainer to="/approvals">
                      <Nav.Link>Aprovações</Nav.Link>
                    </LinkContainer>
                    <NavDropdown title="Relatórios" id="reports-dropdown-purchases">
                      <LinkContainer to="/reports">
                        <NavDropdown.Item>Kanban</NavDropdown.Item>
                      </LinkContainer>
                      <LinkContainer to="/reports/dashboard">
                        <NavDropdown.Item>Dashboard</NavDropdown.Item>
                      </LinkContainer>
                    </NavDropdown>
                    <LinkContainer to="/purchase-export">
                      <Nav.Link>Exportar Requisições</Nav.Link>
                    </LinkContainer>
                  </>
                )}
                {currentArea !== 'projects' && (
                  <LinkContainer to="/knowledge-base">
                    <Nav.Link>Base de Conhecimento</Nav.Link>
                  </LinkContainer>
                )}
                {(userCanManageOpportunities(user?.role) || user?.role === 'admin' || user?.role === 'analista') && (
                  <NavDropdown title="Configurações" id="config-dropdown">
                    <LinkContainer to="/users">
                      <NavDropdown.Item>Usuários</NavDropdown.Item>
                    </LinkContainer>
                    {currentArea !== 'projects' && (
                      <LinkContainer to="/groups">
                        <NavDropdown.Item>Grupos</NavDropdown.Item>
                      </LinkContainer>
                    )}
                    {currentArea !== 'projects' && (
                      <LinkContainer to="/workflows">
                        <NavDropdown.Item>Fluxos</NavDropdown.Item>
                      </LinkContainer>
                    )}
                    {currentArea === 'purchases' && (
                      <LinkContainer to="/document-types">
                        <NavDropdown.Item>Tipos de Documentos</NavDropdown.Item>
                      </LinkContainer>
                    )}
                    {currentArea === 'changes' && changeManager && (
                      <LinkContainer to="/changes/cadastros">
                        <NavDropdown.Item>Cadastros de mudança</NavDropdown.Item>
                      </LinkContainer>
                    )}
                    {currentArea === 'changes' && canViewChangeAudit && (
                      <LinkContainer to="/changes/auditoria">
                        <NavDropdown.Item>Auditoria de mudanças</NavDropdown.Item>
                      </LinkContainer>
                    )}
                    {user?.role === 'admin' && currentArea !== 'projects' && (
                      <LinkContainer to="/holidays">
                        <NavDropdown.Item>Feriados</NavDropdown.Item>
                      </LinkContainer>
                    )}
                    {user?.role === 'admin' && currentArea !== 'projects' && (
                      <LinkContainer to="/admin/tickets-attachments-without-s3">
                        <NavDropdown.Item>Anexos sem S3 (tickets)</NavDropdown.Item>
                      </LinkContainer>
                    )}
                    {user?.role === 'admin' && (
                      <LinkContainer to="/admin/feedbacks">
                        <NavDropdown.Item>Feedbacks (administração)</NavDropdown.Item>
                      </LinkContainer>
                    )}
                    {(user?.role === 'admin' || user?.role === 'analista') && (
                      <LinkContainer to="/config/servicenow-legado">
                        <NavDropdown.Item>Consulta ServiceNow (legado)</NavDropdown.Item>
                      </LinkContainer>
                    )}
                  </NavDropdown>
                )}
              </Nav>
              <Nav className="app-navbar-actions align-items-center">
                <Button
                  variant="outline-light"
                  size="sm"
                  onClick={toggleTheme}
                  className="app-navbar-theme-btn"
                  title={theme === 'light' ? 'Ativar tema escuro' : 'Ativar tema claro'}
                  aria-label={theme === 'light' ? 'Ativar tema escuro' : 'Ativar tema claro'}
                >
                  {theme === 'light' ? '🌙' : '☀️'}
                </Button>
                <NavDropdown
                  title={
                    <span className="app-navbar-user-trigger">
                      <span className="app-navbar-user-name">{user?.name || 'Usuário'}</span>
                      <span className="app-navbar-user-sep" aria-hidden>
                        ·
                      </span>
                      <span className="app-navbar-user-area">
                        {currentArea === 'tickets'
                          ? 'Incidentes'
                          : currentArea === 'purchases'
                          ? 'Compras'
                          : currentArea === 'changes'
                          ? 'Mudanças'
                          : 'Projetos'}
                      </span>
                    </span>
                  }
                  id="user-dropdown"
                  align="end"
                >
                  <NavDropdown.Item disabled>
                    <small>{user?.email}</small>
                  </NavDropdown.Item>
                  <NavDropdown.Divider />
                  <NavDropdown.Header>Área Atual</NavDropdown.Header>
                  <NavDropdown.Item
                    active={currentArea === 'tickets'}
                    onClick={() => handleAreaChange('tickets')}
                    className="app-navbar-dd-item-with-tick"
                  >
                    <span className="app-navbar-dd-tick" aria-hidden>
                      {currentArea === 'tickets' ? '✓' : '\u00a0'}
                    </span>
                    <span className="app-navbar-dd-label">Tickets (Incidentes)</span>
                  </NavDropdown.Item>
                  <NavDropdown.Item
                    active={currentArea === 'purchases'}
                    onClick={() => handleAreaChange('purchases')}
                    className="app-navbar-dd-item-with-tick"
                  >
                    <span className="app-navbar-dd-tick" aria-hidden>
                      {currentArea === 'purchases' ? '✓' : '\u00a0'}
                    </span>
                    <span className="app-navbar-dd-label">Compras e Contratações</span>
                  </NavDropdown.Item>
                  {canSeeProjectsArea && (
                    <NavDropdown.Item
                      active={currentArea === 'projects'}
                      onClick={() => {
                        setArea('projects');
                        navigate('/projects');
                      }}
                      className="app-navbar-dd-item-with-tick"
                    >
                      <span className="app-navbar-dd-tick" aria-hidden>
                        {currentArea === 'projects' ? '✓' : '\u00a0'}
                      </span>
                      <span className="app-navbar-dd-label">Projetos</span>
                    </NavDropdown.Item>
                  )}
                  <NavDropdown.Item
                    active={currentArea === 'changes'}
                    onClick={() => handleAreaChange('changes')}
                    className="app-navbar-dd-item-with-tick"
                  >
                    <span className="app-navbar-dd-tick" aria-hidden>
                      {currentArea === 'changes' ? '✓' : '\u00a0'}
                    </span>
                    <span className="app-navbar-dd-label">Gestão de Mudanças</span>
                  </NavDropdown.Item>
                  <NavDropdown.Divider />
                  <LinkContainer to="/profile">
                    <NavDropdown.Item>Meu Perfil</NavDropdown.Item>
                  </LinkContainer>
                  <NavDropdown.Divider />
                  <NavDropdown.Item onClick={() => void handleLogout()}>
                    Sair
                  </NavDropdown.Item>
                </NavDropdown>
              </Nav>
            </>
          ) : (
            <Nav className="ms-auto">
              <LinkContainer to="/login">
                <Nav.Link>Login</Nav.Link>
              </LinkContainer>
            </Nav>
          )}
        </BootstrapNavbar.Collapse>
      </Container>
    </BootstrapNavbar>
  );
};

export default Navbar;

